<template>
  <section class="ebike-innovation-section">
    <div class="container">
      <div class="innovation-header">
        <div class="innovation-badge">
          <i :class="headerData.icon"></i>
          <span>{{ headerData.tag }}</span>
        </div>
        <h2>{{ headerData.title }}</h2>
        <p class="innovation-subtitle">{{ headerData.description }}</p>
      </div>
      
      <div class="innovation-grid">
        <div class="innovation-card" v-for="(innovation, index) in innovationData" :key="index">
          <div class="card-icon">
            <i :class="innovation.icon"></i>
          </div>
          <div class="card-content">
            <h3>{{ innovation.title }}</h3>
            <p>{{ innovation.description }}</p>
            <div class="tech-features">
              <span v-for="(feature, featureIndex) in innovation.features" :key="featureIndex" class="tech-tag">
                {{ feature }}
              </span>
            </div>
          </div>
          <div class="card-glow"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ServiceItsCardNormal",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-bolt",
        tag: "ELECTRIC INNOVATION",
        title: "Next-Gen E-Bike Services",
        description: "Revolutionary electric bike maintenance with smart technology integration"
      })
    },
    innovationData: {
      type: Array,
      required: true,
      default: () => []
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Electric Bike Innovation Section */
.ebike-innovation-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  position: relative;
  overflow: hidden;
}

.ebike-innovation-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
}

.container {
  position: relative;
  z-index: 2;
}

.innovation-header {
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 2;
}

.innovation-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  color: white;
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
  box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
}

.innovation-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.innovation-subtitle {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.innovation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.innovation-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px 30px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 212, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.innovation-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 212, 255, 0.2);
  border-color: #00d4ff;
  background: rgba(0, 212, 255, 0.1);
}

.card-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
}

.innovation-card:hover .card-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 12px 35px rgba(0, 212, 255, 0.5);
}

.card-icon i {
  font-size: 32px;
  color: white;
}

.card-content {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.card-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: white;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.card-content p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 20px;
  line-height: 1.6;
}

.tech-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
}

.tech-tag {
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.innovation-card:hover .card-glow {
  opacity: 1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .innovation-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .innovation-card {
    padding: 30px 20px;
  }
  
  .innovation-header h2 {
    font-size: 36px;
  }
  
  .innovation-subtitle {
    font-size: 16px;
  }
}
</style>
